/**
 * «Расходы» фирмы — блок `WEB-2.3` (`FR-WEB-601`, решения формы `Б1`–`Б11`,
 * `Б15`–`Б17`, `Б19`, `Б20`): список расходов страницами, отбор по работе и
 * машине, форма записи и правки с чеком, удаление и ссылка на чек.
 *
 * Экран показывает **каждую** запись ответа в порядке ответа. Отбор по работе
 * и машине уходит параметрами запроса (`Б15`): строки клиент не отбирает и не
 * переставляет, пересечение отборов решает сервер, и пустой ответ с отбором —
 * «по этому отбору записей нет» на месте списка. Итогов нет ни в каком виде
 * (`Б4`, развилка Р7 А): сумма — строкой, как пришла, с кодом валюты подписью.
 *
 * Кто вправе вести учёт, решает сервер: список читает только обладатель права,
 * и признака права у записи нет, поэтому «Изменить» и «Удалить» стоят у каждой
 * строки (`Б3`), а отказ ложится словами сервера на месте действия. `403` и
 * `404` списка — побайтово одно «нет доступа» на месте экрана (`FR-WEB-006`).
 *
 * Форма живёт на том же экране (`Б1`): операции чтения одной записи контракт
 * не объявляет, и правка заполняется строкой, которая уже в памяти. Тело —
 * `multipart` (`Б7`), собирает его слой; форма не проверяет ни суммы, ни
 * длины комментария, ни типа и размера чека, ни обязательности полей — всё это
 * `422` сервера под полем (`Б17`). Чек при правке: новый файл заменяет прежний,
 * без файла прежний остаётся, «убрать чек» правкой нельзя (`Б8`).
 *
 * Подпись работы — название и статус подписью из ответа `listWorks` с
 * параметром фирмы (`Б10`): какие работы фирмы, решает сервер, и клиент их не
 * отбирает ни по рынку, ни по статусу, ни по стороне. Работа строки, которой
 * нет на загруженных страницах, подписывается ответом `getWork` — один запрос
 * на идентификатор за жизнь экрана. Цену работы экран не читает (`Б4`).
 *
 * Ссылка на чек — адрес из ответа как есть (`Б9`). Ссылка живёт пять минут:
 * нажатие после срока **из ответа** переход отменяет и перечитывает список.
 */

import { ApiFailure, newIdempotencyKey } from '../api/client';
import type { ApiClient, CompanyExpenseSubmission, ExpensesQuery } from '../api/client';
import { nextPageCursor } from '../api/contract';
import type { CompanyExpense, ExpenseReceipt, Vehicle, Work } from '../api/contract';
import { CURRENCIES, EXPENSE_CATEGORIES } from '../api/generated/enums';
import {
  activeLocale,
  currencyStringKey,
  expenseCategoryStringKey,
  vehicleKindStringKey,
  workStatusStringKey,
} from '../i18n';
import type { Strings } from '../i18n';
import {
  createFieldMessageSlot,
  dateLabel,
  enumLabel,
  moneyLabel,
  placeState,
  refusalText,
  renderFieldMessages,
  screenStateFor,
} from './state';
import type { ScreenState } from './state';

export interface ExpensesScreenOptions {
  readonly client: ApiClient;
  readonly strings: Strings;
  readonly companyId: string;
}

export interface ExpensesScreen {
  readonly element: HTMLElement;
  /** Состояние экрана либо `null` — содержимое показано. */
  readonly state: ScreenState | null;
  /** Записи в том порядке, в котором их вернул сервер, — все страницы подряд. */
  readonly expenses: readonly CompanyExpense[];
  load(): Promise<void>;
  /** Переподписать экран на новом языке, не трогая введённого (долг `TD-72`). */
  relabel(next: Strings): void;
}

/** Вариант выбора: значение провода и подпись. */
interface Choice {
  readonly value: string;
  readonly caption: string;
}

/**
 * Перестроить варианты выбора, сохранив выбранное: смена подписей и подгрузка
 * работ не должны молча менять то, что человек уже выбрал.
 */
function fillSelect(select: HTMLSelectElement, choices: readonly Choice[]): void {
  const chosen = select.value;
  const options: HTMLOptionElement[] = [];

  for (const choice of choices) {
    const option = document.createElement('option');

    option.value = choice.value;
    option.textContent = choice.caption;
    options.push(option);
  }

  select.replaceChildren(...options);
  select.value = chosen;

  if (select.value !== chosen) {
    select.selectedIndex = 0;
  }
}

/** Первые восемь знаков идентификатора — различитель нейтральной подписи. */
function shortId(id: string): string {
  return id.slice(0, 8);
}

/**
 * Протухла ли ссылка на чек к моменту нажатия — срок **из ответа** сервера,
 * своего срока у клиента нет (решение формы `Б9`).
 */
function receiptExpired(receipt: ExpenseReceipt): boolean {
  return Date.now() >= Date.parse(receipt.url_expires_at);
}

/** Поле формы: подпись, элемент и место под сообщение сервера. */
function labelled(caption: HTMLElement, control: HTMLElement, ...extra: HTMLElement[]): HTMLLabelElement {
  const label = document.createElement('label');

  label.className = 'form-field';
  caption.className = 'form-field__caption';
  label.append(caption, control, ...extra, createFieldMessageSlot());

  return label;
}

/** Форма записи и правки: узлы живут, пока форма открыта (долг `TD-72`). */
interface ExpenseForm {
  /** Запись правки либо `null` — новая запись. */
  readonly editing: CompanyExpense | null;
  readonly form: HTMLFormElement;
  readonly category: HTMLSelectElement;
  readonly amount: HTMLInputElement;
  readonly currency: HTMLSelectElement;
  readonly spentOn: HTMLInputElement;
  readonly work: HTMLSelectElement;
  readonly worksMore: HTMLButtonElement;
  readonly vehicle: HTMLSelectElement;
  readonly comment: HTMLTextAreaElement;
  readonly receipt: HTMLInputElement;
  readonly receiptHint: HTMLElement;
  readonly submit: HTMLButtonElement;
  readonly close: HTMLButtonElement;
  readonly captions: Readonly<Record<'category' | 'amount' | 'currency' | 'spentOn' | 'work' | 'vehicle' | 'comment' | 'receipt', HTMLElement>>;
}

export function createExpensesScreen(options: ExpensesScreenOptions): ExpensesScreen {
  const { client, companyId } = options;
  let { strings } = options;

  const element = document.createElement('section');
  const heading = document.createElement('h1');
  const filter = document.createElement('div');
  const filterWorkCaption = document.createElement('span');
  const filterWork = document.createElement('select');
  const filterVehicleCaption = document.createElement('span');
  const filterVehicle = document.createElement('select');
  const filterReset = document.createElement('button');
  const createButton = document.createElement('button');
  const notice = document.createElement('p');
  const formNotice = document.createElement('p');
  const list = document.createElement('ul');
  const more = document.createElement('button');

  element.className = 'screen screen-expenses';
  filter.className = 'expense-filter';
  list.className = 'accounting-list';
  notice.className = 'notice';
  formNotice.className = 'notice';
  createButton.className = 'action';
  filterReset.className = 'action';
  more.className = 'action';
  createButton.type = 'button';
  filterReset.type = 'button';
  more.type = 'button';

  // Выборы отбора называются именами параметров запроса: что значит отбор,
  // решает сервер, клиент только кладёт выбранное в адрес.
  filterWork.name = 'work_id';
  filterVehicle.name = 'vehicle_id';
  filter.append(
    labelled(filterWorkCaption, filterWork),
    labelled(filterVehicleCaption, filterVehicle),
    filterReset,
  );

  let state: ScreenState | null = 'loading';
  /** Пришёл ли ответ сервера — та же величина, что на экране фирмы. */
  let answered = false;
  let expenses: CompanyExpense[] = [];
  let cursor: string | null = null;
  /** Поколение чтения списка: ответ устаревшего отбора к экрану не применяется. */
  let generation = 0;
  let loadingMore = false;

  /** Работы фирмы с загруженных страниц `listWorks` — в порядке ответа. */
  let works: Work[] = [];
  let worksCursor: string | null = null;
  /** Ответила ли лента работ фирмы — до ответа `getWork` не зовётся (`Б10`). */
  let worksSettled = false;
  let worksLoading = false;
  /** Работы вне загруженных страниц: ответ `getWork` либо `null` — отказ. */
  const lookedUp = new Map<string, Work | null>();

  /** Реестр транспорта фирмы либо `null` — ответа нет. */
  let vehicles: readonly Vehicle[] | null = null;

  let form: ExpenseForm | null = null;
  let sending = false;
  /** Ключ идемпотентности текущей отправки записи либо `null`. */
  let idempotencyKey: string | null = null;
  /** Записи, чьё удаление в пути: второй запрос не уходит. */
  const deleting = new Set<string>();

  /** Подписи экрана — одним местом: смена языка переподписывает их на месте. */
  function caption(): void {
    heading.textContent = strings.expenses_title;
    filterWorkCaption.textContent = strings.accounting_field_work;
    filterVehicleCaption.textContent = strings.expense_form_vehicle;
    filterReset.textContent = strings.accounting_filter_reset;
    createButton.textContent = strings.expenses_create;
    more.textContent = strings.accounting_more;

    if (form !== null) {
      form.captions.category.textContent = strings.expense_form_category;
      form.captions.amount.textContent = strings.accounting_field_amount;
      form.captions.currency.textContent = strings.accounting_field_currency;
      form.captions.spentOn.textContent = strings.expense_form_spent_on;
      form.captions.work.textContent = strings.accounting_field_work;
      form.captions.vehicle.textContent = strings.expense_form_vehicle;
      form.captions.comment.textContent = strings.accounting_field_comment;
      form.captions.receipt.textContent = strings.expense_form_receipt;
      form.worksMore.textContent = strings.accounting_works_more;
      form.submit.textContent = strings.expense_form_submit;
      form.close.textContent = strings.accounting_form_close;
      // Имя прежнего чека и подсказка: новый файл заменит прежний, а без файла
      // прежний остаётся — «убрать чек» правкой нельзя (решение формы `Б8`).
      const previous = form.editing === null ? null : form.editing.receipt;

      form.receiptHint.textContent =
        previous === null ? '' : `${previous.name} — ${strings.expense_receipt_replace_hint}`;
    }
  }

  /** Работа по идентификатору: с загруженных страниц либо из ответа `getWork`. */
  function workOf(id: string): Work | null {
    for (const known of works) {
      if (known.id === id) {
        return known;
      }
    }

    return lookedUp.get(id) ?? null;
  }

  /** Подпись работы — название и статус подписью; без ответа — нейтральная. */
  function workCaption(id: string): string {
    const found = workOf(id);

    if (found === null) {
      return `${strings.accounting_work_fallback} ${shortId(id)}`;
    }

    return `${found.title} · ${enumLabel(strings, workStatusStringKey(found.status))}`;
  }

  /** Подпись машины — вид подписью и VIN из реестра (`Б11`). */
  function vehicleCaption(id: string | null): string {
    if (id === null) {
      return strings.accounting_no_vehicle;
    }

    if (vehicles === null) {
      return `${strings.accounting_vehicle_fallback} ${shortId(id)}`;
    }

    for (const known of vehicles) {
      if (known.id === id) {
        return `${enumLabel(strings, vehicleKindStringKey(known.kind))} ${known.vin}`;
      }
    }

    return strings.accounting_vehicle_missing;
  }

  function workChoices(lead: Choice, current: string | null): Choice[] {
    const choices: Choice[] = [lead];
    let present = current === null;

    for (const known of works) {
      choices.push({ value: known.id, caption: workCaption(known.id) });
      present ||= known.id === current;
    }

    // Текущая работа записи стоит отдельным пунктом, даже когда её нет на
    // загруженных страницах: правка суммы не должна молча менять работу.
    if (!present && current !== null) {
      choices.push({ value: current, caption: workCaption(current) });
    }

    return choices;
  }

  function vehicleChoices(lead: Choice, current: string | null): Choice[] {
    const choices: Choice[] = [lead];
    let present = current === null;

    for (const known of vehicles ?? []) {
      choices.push({ value: known.id, caption: vehicleCaption(known.id) });
      present ||= known.id === current;
    }

    if (!present && current !== null) {
      choices.push({ value: current, caption: vehicleCaption(current) });
    }

    return choices;
  }

  function enumChoices(values: readonly string[], keyOf: (value: string) => keyof Strings): Choice[] {
    const choices: Choice[] = [{ value: '', caption: strings.accounting_choose }];

    for (const value of values) {
      choices.push({ value, caption: enumLabel(strings, keyOf(value)) });
    }

    return choices;
  }

  /** Строка списка: дата, категория, сумма, работа, машина, комментарий, чек. */
  function row(expense: CompanyExpense): HTMLElement {
    const node = document.createElement('li');
    const line = document.createElement('span');
    const edit = document.createElement('button');
    const remove = document.createElement('button');
    const parts = [
      dateLabel(activeLocale(), expense.spent_on),
      enumLabel(strings, expenseCategoryStringKey(expense.category)),
      moneyLabel(strings, expense.amount),
      workCaption(expense.work_id),
      vehicleCaption(expense.vehicle_id),
    ];

    if (expense.comment !== null) {
      parts.push(expense.comment);
    }

    node.className = 'expense-row';
    line.textContent = parts.join(' · ');
    node.append(line);

    if (expense.receipt !== null) {
      node.append(receiptLink(expense.receipt));
    }

    edit.type = 'button';
    edit.className = 'action';
    edit.textContent = strings.accounting_edit;
    edit.addEventListener('click', () => {
      openForm(expense);
    });

    remove.type = 'button';
    remove.className = 'action';
    remove.textContent = strings.accounting_delete;
    remove.disabled = deleting.has(expense.id);
    remove.addEventListener('click', () => {
      void deleteExpense(expense.id);
    });

    node.append(edit, remove);

    return node;
  }

  /**
   * Ссылка на чек — адрес ответа как есть, новая вкладка (`Б9`). Нажатие после
   * срока из ответа переход отменяет: список перечитывается тем же отбором, и
   * новая ссылка приходит со своим сроком.
   */
  function receiptLink(receipt: ExpenseReceipt): HTMLAnchorElement {
    const link = document.createElement('a');

    link.className = 'company-link';
    link.href = receipt.url;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = receipt.name;
    link.addEventListener('click', (event) => {
      if (!receiptExpired(receipt)) {
        return;
      }

      event.preventDefault();
      notice.textContent = strings.expense_receipt_refreshed;
      void reread();
    });

    return link;
  }

  function render(): void {
    fillSelect(filterWork, workChoices({ value: '', caption: strings.accounting_filter_any }, null));
    fillSelect(filterVehicle, vehicleChoices({ value: '', caption: strings.accounting_filter_any }, null));

    if (form !== null) {
      const editing = form.editing;

      fillSelect(form.category, enumChoices(EXPENSE_CATEGORIES, expenseCategoryStringKey));
      fillSelect(form.currency, enumChoices(CURRENCIES, currencyStringKey));
      fillSelect(form.work, workChoices({ value: '', caption: strings.accounting_choose }, editing?.work_id ?? null));
      fillSelect(
        form.vehicle,
        vehicleChoices({ value: '', caption: strings.accounting_no_vehicle }, editing?.vehicle_id ?? null),
      );
      form.worksMore.hidden = worksCursor === null;
      form.worksMore.disabled = worksLoading;
    }

    list.replaceChildren(...expenses.map(row));
    more.disabled = loadingMore;

    const frame: HTMLElement[] = [heading, filter, createButton, notice, formNotice];
    const chosen = chosenFilter();
    const filtered = chosen.work_id !== undefined || chosen.vehicle_id !== undefined;

    if (form !== null) {
      frame.push(form.form);
    }

    // Правило места состояния — одна функция на все экраны (долг `TD-66`):
    // ответа нет — ни заголовка, ни отбора, ни «Записать», и отказ прав с
    // отсутствием фирмы дают побайтово один экран; ответ пустой — подпись
    // встаёт на место **списка**, отбор и форма остаются.
    placeState(
      element,
      {
        state,
        answered,
        frame,
        list,
        after: cursor === null ? [] : [more],
        // Пустой ответ с отбором — «по этому отбору записей нет»; без отбора —
        // «расходов пока нет». Ответ пришёл в обоих случаях.
        emptyCaption: filtered ? strings.accounting_filter_empty : strings.expenses_empty,
      },
      strings,
    );
  }

  /** Отбор из выборов: пустой выбор — параметра нет вовсе. */
  function chosenFilter(): ExpensesQuery {
    const query: { work_id?: string; vehicle_id?: string } = {};

    if (filterWork.value !== '') {
      query.work_id = filterWork.value;
    }

    if (filterVehicle.value !== '') {
      query.vehicle_id = filterVehicle.value;
    }

    return query;
  }

  /** Работы строк, которых нет на загруженных страницах, — `getWork` по разу. */
  function lookUpMissingWorks(): void {
    if (!worksSettled) {
      return;
    }

    for (const expense of expenses) {
      const id = expense.work_id;

      if (workOf(id) !== null || lookedUp.has(id)) {
        continue;
      }

      lookedUp.set(id, null);
      void client
        .getWork(id)
        .then((found) => {
          lookedUp.set(id, found);
          render();
        })
        .catch(() => {
          // Отказ — нейтральная подпись «Работа» и восемь знаков; повторного
          // запроса за жизнь экрана нет.
        });
    }
  }

  /** Чтение первой страницы с текущим отбором — и первое, и всякое следующее. */
  async function readFirstPage(): Promise<void> {
    generation += 1;
    const mine = generation;

    try {
      const page = await client.listCompanyExpenses(companyId, chosenFilter());

      if (mine !== generation) {
        return;
      }

      expenses = [...page.data];
      cursor = nextPageCursor(page.meta);
      answered = true;
      state = screenStateFor({ kind: 'done', empty: page.data.length === 0 });
      lookUpMissingWorks();
    } catch (error) {
      if (mine !== generation) {
        return;
      }

      expenses = [];
      cursor = null;
      answered = false;
      state = screenStateFor({
        kind: 'failed',
        code: error instanceof ApiFailure ? error.code : null,
      });
    }

    render();
  }

  /** Страница работ фирмы — параметром фирмы, порядок сервера (`Б10`). */
  async function readWorks(next: string | null): Promise<void> {
    worksLoading = true;
    render();

    try {
      // Фирма уходит параметром запроса: какие работы её, решает сервер.
      const query: { company_id: string; cursor?: string } = { company_id: companyId };

      if (next !== null) {
        query.cursor = next;
      }

      const page = await client.listWorks(query);

      works = [...works, ...page.data];
      worksCursor = nextPageCursor(page.meta);
    } catch (error) {
      if (form !== null) {
        formNotice.textContent = refusalText(error, strings);
      }
    } finally {
      worksLoading = false;
      worksSettled = true;
    }

    lookUpMissingWorks();
    render();
  }

  async function readVehicles(): Promise<void> {
    try {
      vehicles = await client.listCompanyVehicles(companyId);
    } catch {
      vehicles = null;
    }

    render();
  }

  async function load(): Promise<void> {
    state = screenStateFor({ kind: 'pending' });
    answered = false;
    expenses = [];
    cursor = null;
    works = [];
    worksCursor = null;
    worksSettled = false;
    vehicles = null;
    form = null;
    notice.textContent = '';
    formNotice.textContent = '';
    render();

    await Promise.all([readFirstPage(), readWorks(null), readVehicles()]);
  }

  /**
   * Перечитывание с первой страницы тем же отбором — после записи, правки,
   * удаления, смены отбора и протухшей ссылки (`Б16`): клиент строк не
   * дописывает и не вычёркивает сам. Экран не уходит в «загрузку».
   */
  async function reread(): Promise<void> {
    await readFirstPage();
  }

  /** «Показать ещё» — та же операция, курсор и тот же отбор; страница — после прежней. */
  async function loadMore(): Promise<void> {
    const from = cursor;

    if (from === null || loadingMore) {
      return;
    }

    const mine = generation;

    loadingMore = true;
    notice.textContent = '';
    render();

    try {
      const page = await client.listCompanyExpenses(companyId, { ...chosenFilter(), cursor: from });

      if (mine === generation) {
        expenses = [...expenses, ...page.data];
        cursor = nextPageCursor(page.meta);
        lookUpMissingWorks();
      }
    } catch (error) {
      // Отказ второй страницы не стирает первую: текст — на месте действия.
      notice.textContent = refusalText(error, strings);
    } finally {
      loadingMore = false;
    }

    render();
  }

  /** Сервер ответил отказом — следующая отправка новая и несёт новый ключ. */
  function serverAnswered(error: unknown): boolean {
    return error instanceof ApiFailure && error.code !== null;
  }

  /**
   * Отказ **формы** — словами сервера (`Б17`): сообщения по полям — под полями,
   * которые сервер назвал; непоказанное и общий отказ — над формой. Введённое
   * остаётся: узлы формы не пересоздаются.
   */
  function formRefused(target: HTMLFormElement, error: unknown): void {
    const failure = error instanceof ApiFailure ? error : null;
    const homeless = renderFieldMessages(target, failure?.details ?? []);
    const texts: string[] = [];

    for (const detail of homeless) {
      texts.push(detail.message);
    }

    if (texts.length === 0) {
      texts.push(refusalText(error, strings));
    }

    formNotice.textContent = texts.join(' ');
  }

  /** Тело формы — части контракта как введены; машина, комментарий, чек — по выбору. */
  function entered(shown: ExpenseForm): CompanyExpenseSubmission {
    const commentText = shown.comment.value;

    return {
      category: shown.category.value,
      amount: shown.amount.value,
      currency: shown.currency.value,
      spent_on: shown.spentOn.value,
      work_id: shown.work.value,
      vehicle_id: shown.vehicle.value === '' ? null : shown.vehicle.value,
      comment: commentText.trim() === '' ? null : commentText,
      receipt: shown.receipt.files?.item(0) ?? null,
    };
  }

  async function submitForm(shown: ExpenseForm): Promise<void> {
    sending = true;
    shown.submit.disabled = true;
    formNotice.textContent = '';
    notice.textContent = '';
    renderFieldMessages(shown.form, []);

    const input = entered(shown);

    try {
      if (shown.editing === null) {
        idempotencyKey ??= newIdempotencyKey();
        await client.createCompanyExpense(companyId, input, idempotencyKey);
      } else {
        await client.updateCompanyExpense(shown.editing.id, input);
      }

      idempotencyKey = null;
      form = null;
      await reread();
    } catch (error) {
      if (serverAnswered(error)) {
        idempotencyKey = null;
      }

      formRefused(shown.form, error);
    } finally {
      sending = false;
      shown.submit.disabled = false;
    }
  }

  /**
   * Удаление — после подтверждения; пока запрос в пути, «Удалить» неактивна.
   * Ответ сервера любой — список перечитывается, отказ ложится его словами;
   * без конверта (обрыв связи) перечитывать нечем.
   */
  async function deleteExpense(expenseId: string): Promise<void> {
    if (deleting.has(expenseId) || !window.confirm(strings.expenses_delete_confirm)) {
      return;
    }

    deleting.add(expenseId);
    notice.textContent = '';
    render();

    let failure: unknown = null;

    try {
      await client.deleteCompanyExpense(expenseId);
    } catch (error) {
      failure = error;
    } finally {
      deleting.delete(expenseId);
    }

    if (failure !== null) {
      notice.textContent = refusalText(failure, strings);

      if (!serverAnswered(failure)) {
        render();

        return;
      }
    }

    await reread();
  }

  /** Форма записи (`editing` — `null`) или правки строки, что уже в памяти. */
  function buildForm(editing: CompanyExpense | null): ExpenseForm {
    const shown: ExpenseForm = {
      editing,
      form: document.createElement('form'),
      category: document.createElement('select'),
      amount: document.createElement('input'),
      currency: document.createElement('select'),
      spentOn: document.createElement('input'),
      work: document.createElement('select'),
      worksMore: document.createElement('button'),
      vehicle: document.createElement('select'),
      comment: document.createElement('textarea'),
      receipt: document.createElement('input'),
      receiptHint: document.createElement('span'),
      submit: document.createElement('button'),
      close: document.createElement('button'),
      captions: {
        category: document.createElement('span'),
        amount: document.createElement('span'),
        currency: document.createElement('span'),
        spentOn: document.createElement('span'),
        work: document.createElement('span'),
        vehicle: document.createElement('span'),
        comment: document.createElement('span'),
        receipt: document.createElement('span'),
      },
    };

    // Имена полей — имена частей формы контракта: по ним сервер называет место
    // своего сообщения. Ни `accept`, ни пределов у полей нет — судит сервер.
    shown.form.className = 'form expense-form';
    shown.category.name = 'category';
    shown.amount.name = 'amount';
    shown.amount.type = 'text';
    shown.amount.inputMode = 'decimal';
    shown.currency.name = 'currency';
    shown.spentOn.name = 'spent_on';
    shown.spentOn.type = 'date';
    shown.work.name = 'work_id';
    shown.vehicle.name = 'vehicle_id';
    shown.comment.name = 'comment';
    shown.receipt.name = 'receipt';
    shown.receipt.type = 'file';
    shown.receiptHint.className = 'form-hint';
    shown.worksMore.type = 'button';
    shown.worksMore.className = 'action';
    shown.submit.type = 'submit';
    shown.submit.className = 'action';
    shown.close.type = 'button';
    shown.close.className = 'action';

    shown.form.append(
      labelled(shown.captions.category, shown.category),
      labelled(shown.captions.amount, shown.amount),
      labelled(shown.captions.currency, shown.currency),
      labelled(shown.captions.spentOn, shown.spentOn),
      labelled(shown.captions.work, shown.work),
      shown.worksMore,
      labelled(shown.captions.vehicle, shown.vehicle),
      labelled(shown.captions.comment, shown.comment),
      labelled(shown.captions.receipt, shown.receipt, shown.receiptHint),
      shown.submit,
      shown.close,
    );

    // Правка любого поля — новая отправка: ключ прежней снимается (`Б5`).
    shown.form.addEventListener('input', () => {
      idempotencyKey = null;
    });
    shown.form.addEventListener('change', () => {
      idempotencyKey = null;
    });
    shown.form.addEventListener('submit', (event) => {
      event.preventDefault();

      if (sending) {
        return;
      }

      void submitForm(shown);
    });
    shown.worksMore.addEventListener('click', () => {
      if (worksLoading || worksCursor === null) {
        return;
      }

      void readWorks(worksCursor);
    });
    shown.close.addEventListener('click', () => {
      form = null;
      formNotice.textContent = '';
      render();
    });

    return shown;
  }

  /** Открыть форму: новая запись либо правка строки значениями записи. */
  function openForm(editing: CompanyExpense | null): void {
    const shown = buildForm(editing);

    form = shown;
    idempotencyKey = null;
    formNotice.textContent = '';
    caption();
    render();

    if (editing !== null) {
      shown.category.value = editing.category;
      shown.amount.value = editing.amount.amount;
      shown.currency.value = editing.amount.currency;
      shown.spentOn.value = editing.spent_on;
      shown.work.value = editing.work_id;
      shown.vehicle.value = editing.vehicle_id ?? '';
      shown.comment.value = editing.comment ?? '';
    }
  }

  createButton.addEventListener('click', () => {
    openForm(null);
  });

  // Смена отбора — перечитывание с первой страницы тем же запросом сервера.
  filterWork.addEventListener('change', () => {
    void reread();
  });
  filterVehicle.addEventListener('change', () => {
    void reread();
  });
  filterReset.addEventListener('click', () => {
    filterWork.value = '';
    filterVehicle.value = '';
    void reread();
  });

  more.addEventListener('click', () => {
    void loadMore();
  });

  /**
   * Переподписать экран на новом языке (долг `TD-72`): форма остаётся теми же
   * узлами с введённым и выбранным файлом, меняются только подписи.
   */
  function relabel(next: Strings): void {
    strings = next;
    caption();
    render();
  }

  caption();
  render();

  return {
    element,

    get state(): ScreenState | null {
      return state;
    },

    get expenses(): readonly CompanyExpense[] {
      return expenses;
    },

    load,
    relabel,
  };
}
